<style scoped>
.usuario-mantenedor {
  padding: 20px;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
}

h1 {
  color: #333;
  margin-bottom: 20px;
}

.content {
  background: #f9f9f9;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}
</style>

<template>
  <div class="usuario-mantenedor">
    <div
      style="
        padding: 20px;
        margin: 20px;
        background-color: white;
        border-radius: 10px;
      "
    >
      <h2>Mantenedor de Usuarios</h2>

      <!-- Toolbar -->
      <div class="d-flex flex-wrap ga-3 mb-3">
        <v-row>
          <v-col cols="12" md="8">
            <label>Empleado:</label>
            <v-combobox
              v-model="empleadoSel"
              :items="lstEmpleados"
              variant="solo"
              autocomplete="off"
              hide-details
              pa-1
              clearable
              ma-0
              item-title="rutDisplayName"
              item-value="personId"
            >
            </v-combobox>
          </v-col>
          <v-col cols="12" md="4" class="text-right">
            <v-btn
              @click="addEmpleado"
              style="
                background-color: #a3a3a3;
                padding: 15px;
                border-radius: 15px;
                margin-top: 2.4em;
                color: white;
                display: flex;
              "
              dark
            >
              Guardar
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <br />
      <h2>Usuarios</h2>
      <div class="d-flex flex-wrap ga-3 mb-3">
        <div class="w-in">
          <v-text-field
            v-model="search"
            label="Buscar por rut, nombre, email"
            prepend-inner-icon="mdi-magnify"
            single-line
            variant="solo"
            autocomplete="off"
            hide-details
          ></v-text-field>
        </div>
      </div>
      <!-- Data Table -->
      <v-card>
        <v-data-table
          :headers="headers"
          :items="lstUsuarios"
          :loading="loading"
          :search="search"
          item-value="id"
          class="elevation-1"
        >
          <template v-slot:item.actions="{ item }">
            <v-btn
              icon
              size="small"
              color="error"
              variant="text"
              @click="confirmDelete(item)"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>

          <template v-slot:no-data>
            <v-alert type="info" variant="text">
              No hay usuarios registrados
            </v-alert>
          </template>

          <template v-slot:loading>
            <v-skeleton-loader type="table-row@10"></v-skeleton-loader>
          </template>
        </v-data-table>
      </v-card>

      <v-dialog v-model="confirmDialog" max-width="400px">
        <v-card>
          <v-card-title>
            <span class="text-h6">Confirmar Eliminación</span>
          </v-card-title>
          <v-card-text>
            ¿Está seguro que desea eliminar al usuario "{{
              usuarioToDelete?.fullName
            }}"?
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" variant="text" @click="confirmDialog = false">
              Cancelar
            </v-btn>
            <v-btn
              color="error"
              variant="flat"
              @click="deleteUsuarioConfirmed"
              :loading="deleting"
            >
              Eliminar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import swal from "sweetalert2";
import useUsuario from "../composables/useUsuario";
import useEmpleado from "../composables/useEmpleado";
import type { Usuario } from "../types/usuario";
import type { Empleado } from "../types/empleado";

// Composables
const { getUsuarios, postUsuario, deleteUsuario, lstUsuarios } = useUsuario();
const { getEmpleados, lstEmpleados } = useEmpleado();

// Data table headers
let headers: any = [
  { title: "Nombre", key: "fullName", align: "start" },
  { title: "Rut", key: "rut", align: "start" },
  { title: "Email", key: "email", align: "start" },
  {
    title: "Acciones",
    key: "actions",
    align: "center",
    sortable: false,
    width: "120px",
  },
];

// Reactive variables
const loading = ref(false);
const search = ref("");
// const dialog = ref(false);
const confirmDialog = ref(false);
// const valid = ref(false);
// const isEdit = ref(false);
const saving = ref(false);
const deleting = ref(false);
const empleadoSel = ref<Empleado | null>(null);

const defaultUsuario: Usuario = {
  id: 0,
  fullName: "",
  rut: "",
  email: "",
};

const editedUsuario = ref<Usuario>({ ...defaultUsuario });
const usuarioToDelete = ref<Usuario | null>(null);

onMounted(async () => {
  await getEmpleados();
  await getUsuarios();
});

async function addEmpleado() {
  if (!empleadoSel.value) {
    swal.fire({
      icon: "error",
      title: "Error",
      text: "Debe seleccionar un empleado.",
    });
    return;
  }

  const existingUser = lstUsuarios.value.find(
    (u) => u.email === empleadoSel.value?.email
  );
  if (existingUser) {
    swal.fire({
      icon: "error",
      title: "Error",
      text: "El empleado ya tiene un usuario asociado.",
    });
    return;
  }

  editedUsuario.value = {
    id: 0,
    fullName: empleadoSel.value.fullName,
    rut: empleadoSel.value.rut,
    email: empleadoSel.value.email,
  };

  let posted = await postUsuario(editedUsuario.value);

  if (posted) {
    swal.fire({
      icon: "success",
      title: "Éxito",
      text: "Empleado asignado exitosamente.",
      showConfirmButton: false,
      timer: 2000,
    });
    await getUsuarios();
  } else {
    swal.fire({
      icon: "error",
      title: "Error",
      text: "Hubo un problema al crear el usuario.",
    });
  }
  empleadoSel.value = null;
}

const confirmDelete = (usuario: Usuario) => {
  usuarioToDelete.value = usuario;
  confirmDialog.value = true;
};

const deleteUsuarioConfirmed = async () => {
  const id = usuarioToDelete.value?.id;
  if (id == null) return;

  deleting.value = true;
  try {
    await deleteUsuario(id);
    confirmDialog.value = false;
    await getUsuarios();
  } catch (error) {
  } finally {
    deleting.value = false;
    usuarioToDelete.value = null;
  }
};
</script>
